/**
 * Turn one page of provenance files into the Create New Threat Report modal's
 * Fivecast and Magnet export lists.
 */

/** The subset of a provenance ``FileSummary`` the export lists are built from. */
export type ThreatReportSourceItem = {
  fileUrl?: string;
  searchablePath?: string;
  contentCategories?: string[];
};

export type ThreatReportSourceLists = {
  fiveCastFiles: string[];
  magnetFiles: string[];
};

/**
 * Why the lists are empty, when the reason is not "this case has no exports".
 * Empty lists alone cannot be reported to the analyst as "no suitable data
 * source": the lookup may never have run.
 *
 * - ``session`` — no cell id / access token, so nothing was requested.
 * - ``all-cases`` — the cross-case selection, which has no ``caseTag``: a
 *   report is built for one case, so the analyst has to pick one.
 * - ``cases-unavailable`` — the cases list could not be loaded, so no tag could
 *   be resolved from it.
 * - ``case`` — the case is not in the loaded cases list, so there is no
 *   ``caseTag`` to filter provenance by.
 * - ``failed`` — the provenance lookup itself errored.
 */
export type ThreatReportSourcesError =
  | 'session'
  | 'all-cases'
  | 'cases-unavailable'
  | 'case'
  | 'failed'
  | null;

/**
 * Canonical storage URL for a provenance item. ``fileUrl`` is the ``s3://`` URL;
 * ``searchablePath`` is only a fallback for older records that predate it.
 */
function storageUrl(item: ThreatReportSourceItem): string {
  const fileUrl = typeof item.fileUrl === 'string' ? item.fileUrl.trim() : '';
  if (fileUrl) return fileUrl;
  const searchable =
    typeof item.searchablePath === 'string' ? item.searchablePath.trim() : '';
  if (searchable.startsWith('s3://') || searchable.startsWith('files/')) {
    return searchable;
  }
  return '';
}

/**
 * Both the creators lookup and assistant-service key a Fivecast export off the
 * export zip's own storage URI, so only a zip can be reported on. Files
 * extracted from an export inherit its ``fivecast`` content category, and would
 * otherwise list here as exports that resolve to no accounts.
 */
function isFivecastExport(url: string): boolean {
  return url.startsWith('s3://') && url.toLowerCase().endsWith('.zip');
}

/**
 * Partition provenance files by content category, in the order provenance
 * returned them and without duplicate URLs.
 */
export function partitionThreatReportSources(
  items: ThreatReportSourceItem[],
): ThreatReportSourceLists {
  const fiveCastFiles: string[] = [];
  const magnetFiles: string[] = [];
  const seenFivecast = new Set<string>();
  const seenMagnet = new Set<string>();

  for (const item of items) {
    const url = storageUrl(item);
    if (!url) continue;
    const categories = (item.contentCategories || []).map((category) =>
      String(category).toLowerCase(),
    );
    if (
      categories.includes('fivecast') &&
      isFivecastExport(url) &&
      !seenFivecast.has(url)
    ) {
      seenFivecast.add(url);
      fiveCastFiles.push(url);
    }
    if (categories.includes('magnet') && !seenMagnet.has(url)) {
      seenMagnet.add(url);
      magnetFiles.push(url);
    }
  }

  return { fiveCastFiles, magnetFiles };
}

/**
 * Creation time embedded in a Fivecast export name.
 *
 * Standardized names match assistant-service ``export_source_time`` (TR-4016):
 * ``INV-…_(Investigation|Job|Entity)_YYYY-MM-DDTHHMMSSZ.zip``.
 * Older deliveries use ``export_json`` or ``export_anon`` plus ``YYYYMMDD``,
 * optionally ``_HHMMSS``. This is the export's generation time, not the S3
 * upload time and not the time a threat report is generated from the export.
 */
const FIVECAST_EXPORT_BASENAME =
  /^INV-.+_(?:Investigation|Job|Entity)_(\d{4}-\d{2}-\d{2}T\d{6}Z)\.zip$/;
const LEGACY_FIVECAST_EXPORT_BASENAME =
  /(?:^|[._])export_(?:json|anon)_(\d{4})(\d{2})(\d{2})(?:_(\d{2})(\d{2})(\d{2}))?\.zip$/i;

export type FivecastExportStamp = {
  at: Date;
  /** ``day`` when the filename has a date and no clock time. */
  precision: 'day' | 'second';
  /** ``filename`` is the export's generation time. ``uploaded`` is the S3 listing time. */
  origin: 'filename' | 'uploaded';
};

function utcDate(
  year: number,
  month: number,
  day: number,
  hours = 0,
  minutes = 0,
  seconds = 0,
): Date | null {
  const parsed = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() + 1 !== month ||
    parsed.getUTCDate() !== day ||
    parsed.getUTCHours() !== hours ||
    parsed.getUTCMinutes() !== minutes ||
    parsed.getUTCSeconds() !== seconds
  ) {
    return null;
  }
  return parsed;
}

function fivecastExportBasename(sourceUrl: string | undefined): string {
  if (!sourceUrl) return '';
  let basename = sourceUrl.replace(/\/+$/, '').split('/').pop() || '';
  try {
    basename = decodeURIComponent(basename);
  } catch {
    // Keep the raw segment when it is not valid percent-encoding.
  }
  return basename;
}

export function fivecastExportStamp(sourceUrl: string | undefined): FivecastExportStamp | null {
  const basename = fivecastExportBasename(sourceUrl);
  if (!basename) return null;

  const standardized = FIVECAST_EXPORT_BASENAME.exec(basename);
  if (standardized) {
    const stamp = standardized[1];
    const iso = stamp.replace(/T(\d{2})(\d{2})(\d{2})Z$/, 'T$1:$2:$3Z');
    const [datePart, timePart] = iso.slice(0, -1).split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    const at = utcDate(year, month, day, hours, minutes, seconds);
    return at ? { at, precision: 'second', origin: 'filename' } : null;
  }

  const legacy = LEGACY_FIVECAST_EXPORT_BASENAME.exec(basename);
  if (!legacy) return null;
  const year = Number(legacy[1]);
  const month = Number(legacy[2]);
  const day = Number(legacy[3]);
  const hasTime = legacy[4] != null;
  const at = utcDate(
    year,
    month,
    day,
    hasTime ? Number(legacy[4]) : 0,
    hasTime ? Number(legacy[5]) : 0,
    hasTime ? Number(legacy[6]) : 0,
  );
  if (!at) return null;
  return { at, precision: hasTime ? 'second' : 'day', origin: 'filename' };
}

export function fivecastExportCreatedAt(sourceUrl: string | undefined): Date | null {
  return fivecastExportStamp(sourceUrl)?.at ?? null;
}

/** Upload times from ``getSources`` ``sourceModifiedAt``, keyed by storage URL. */
export function sourceModifiedAtFromGetSourcesResponse(
  response: Record<string, unknown> | null | undefined,
): Record<string, string> {
  const sources = response?.sources;
  if (!sources || typeof sources !== 'object') return {};
  const raw = (sources as Record<string, unknown>).sourceModifiedAt;
  if (!raw || typeof raw !== 'object') return {};
  const dates: Record<string, string> = {};
  for (const [url, value] of Object.entries(raw as Record<string, unknown>)) {
    const trimmedUrl = url.trim();
    const at = typeof value === 'string' ? value.trim() : '';
    if (!trimmedUrl || !at || Number.isNaN(Date.parse(at))) continue;
    dates[trimmedUrl] = at;
  }
  return dates;
}

export type ThreatReportSourceStamp = FivecastExportStamp;

/**
 * Date for a threat-report title: the export's generation time when the
 * filename carries it, otherwise the upload time from ``getSources``
 * ``sourceModifiedAt``. Never the time the report itself is generated.
 */
export function threatReportSourceStamp(
  sourceUrl: string | undefined,
  uploadedAtByUrl: Record<string, string>,
): ThreatReportSourceStamp | null {
  const created = fivecastExportStamp(sourceUrl);
  if (created) return created;
  if (!sourceUrl) return null;
  const uploaded = uploadedAtByUrl[sourceUrl];
  if (!uploaded) return null;
  const parsed = new Date(uploaded);
  return Number.isFinite(parsed.getTime()) ? { at: parsed, precision: 'second', origin: 'uploaded' } : null;
}

/**
 * Where a suggested threat-report title got its date, so the create form can
 * say so. ``investigation`` is a standardized ``INV-…`` name; ``filename-time``
 * and ``filename-day`` are older ``export_json`` / ``export_anon`` names.
 */
export type ThreatReportTitleDateBasis =
  | 'investigation'
  | 'filename-time'
  | 'filename-day'
  | 'uploaded'
  | 'unavailable'
  | 'no-fivecast';

export function threatReportTitleDateBasis(
  sourceUrl: string | undefined,
  uploadedAtByUrl: Record<string, string>,
): ThreatReportTitleDateBasis {
  if (!sourceUrl) return 'no-fivecast';
  const stamp = threatReportSourceStamp(sourceUrl, uploadedAtByUrl);
  if (!stamp) return 'unavailable';
  if (stamp.origin === 'uploaded') return 'uploaded';
  if (FIVECAST_EXPORT_BASENAME.test(fivecastExportBasename(sourceUrl))) return 'investigation';
  return stamp.precision === 'day' ? 'filename-day' : 'filename-time';
}

export function threatReportSourceDate(
  sourceUrl: string | undefined,
  uploadedAtByUrl: Record<string, string>,
): Date | null {
  return threatReportSourceStamp(sourceUrl, uploadedAtByUrl)?.at ?? null;
}

/**
 * Merge two export lists. ``primary`` wins on duplicates — its URLs are the
 * canonical ``getSources`` / ``getSelectors`` keys; ``supplemental`` (provenance)
 * only adds exports the S3 listing missed.
 */
export function mergeThreatReportSourceLists(
  primary: ThreatReportSourceLists,
  supplemental: ThreatReportSourceLists,
): ThreatReportSourceLists {
  const fiveCastFiles = [...primary.fiveCastFiles];
  const magnetFiles = [...primary.magnetFiles];
  const seenFivecast = new Set(fiveCastFiles);
  const seenMagnet = new Set(magnetFiles);

  for (const url of supplemental.fiveCastFiles) {
    if (!seenFivecast.has(url)) {
      seenFivecast.add(url);
      fiveCastFiles.push(url);
    }
  }
  for (const url of supplemental.magnetFiles) {
    if (!seenMagnet.has(url)) {
      seenMagnet.add(url);
      magnetFiles.push(url);
    }
  }

  return { fiveCastFiles, magnetFiles };
}

/** Normalise a ``getSources`` response body onto {@link ThreatReportSourceLists}. */
export function threatReportSourceListsFromGetSourcesResponse(
  response: Record<string, unknown> | null | undefined,
): ThreatReportSourceLists {
  const sources = response?.sources;
  if (!sources || typeof sources !== 'object') {
    return { fiveCastFiles: [], magnetFiles: [] };
  }
  const raw = sources as Record<string, unknown>;
  const fiveCastFiles = Array.isArray(raw.fiveCastFiles)
    ? raw.fiveCastFiles
        .filter((url): url is string => typeof url === 'string')
        .map((url) => url.trim())
        .filter(Boolean)
    : [];
  const magnetFiles = Array.isArray(raw.magnetFiles)
    ? raw.magnetFiles
        .filter((url): url is string => typeof url === 'string')
        .map((url) => url.trim())
        .filter(Boolean)
    : [];
  return { fiveCastFiles, magnetFiles };
}
